import React, { useState, useEffect } from 'react';
import { Typography, Paper, CircularProgress, makeStyles, Theme, Button } from '@material-ui/core';
import ErrorIcon from '@mui/icons-material/Error';
import RefreshIcon from '@mui/icons-material/Refresh';

import useStreamingText from '../hooks/useStreamingText';
import { useTemplateParser } from '../hooks/ImpactAiContextProvider';

interface StreamingMessageProps {
  text: string;
  isAnimating?: boolean;
  className?: string;
  onAnimationComplete?: () => void;
  wordDelay?: number;
  customStyle?: React.CSSProperties;
  showSpinner?: boolean;
  spinnerTime?: number;
  isError?: boolean;
  onRetry?: () => void;
}

const useStyles = makeStyles((theme: Theme) => ({
  loadingContainer: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: '100%',
    padding: theme.spacing(1),
    minHeight: 24,
  },
  messageText: {
    wordBreak: 'break-word',
    whiteSpace: 'pre-wrap',
  },
  errorContainer: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: theme.spacing(2),
    padding: theme.spacing(2),
    borderRadius: theme.shape.borderRadius,
  },
  errorMessage: {
    color: theme.palette.error.main,
    textAlign: 'center',
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1),
  },
  retryButton: {
    backgroundColor: theme.palette.error.main,
    color: theme.palette.error.contrastText,
    '&:hover': {
      backgroundColor: theme.palette.error.dark,
    },
  },
}));

const StreamingMessage: React.FC<StreamingMessageProps> = ({
  text,
  isAnimating = false,
  className = '',
  onAnimationComplete,
  wordDelay = 50,
  customStyle,
  showSpinner = false,
  spinnerTime = 0,
  isError = false,
  onRetry,
}) => {
  const classes = useStyles();
  const [isThinking, setIsThinking] = useState(showSpinner);
  const parseTemplate = useTemplateParser();

  const parsedText = parseTemplate(text);

  useEffect(() => {
    if (showSpinner && spinnerTime > 0) {
      const timer = setTimeout(() => {
        setIsThinking(false);
      }, spinnerTime);

      return () => clearTimeout(timer);
    }
    return undefined;
  }, [showSpinner, spinnerTime]);

  const { streamedText } = useStreamingText(parsedText, isAnimating && !isThinking, {
    wordDelay,
    onComplete: onAnimationComplete,
  });

  return (
    <Paper className={className} style={customStyle}>
      {isError ? (
        <div className={classes.errorContainer}>
          <div className={classes.errorMessage}>
            <ErrorIcon />
            <Typography className={classes.messageText}>
              {isAnimating ? streamedText : parsedText}
            </Typography>
          </div>
          {onRetry && (
            <Button
              variant="contained"
              className={classes.retryButton}
              startIcon={<RefreshIcon />}
              onClick={() => {
                onRetry();
              }}
            >
              Try Again
            </Button>
          )}
        </div>
      ) : isThinking ? (
        <div className={classes.loadingContainer}>
          <CircularProgress size={20} />
        </div>
      ) : (
        <Typography className={classes.messageText}>
          {isAnimating ? streamedText : parsedText}
        </Typography>
      )}
    </Paper>
  );
};

export default StreamingMessage;
